import { useEffect, useRef, useState } from 'react'
import { Loader2, Film } from 'lucide-react'
import { getCompanyVideo } from './companyVideos.service'

// In-memory cache for thumbnail URLs to avoid re-fetching on navigation
const thumbnailCache = new Map<string, { url: string; type: 'image' | 'video' }>()

interface VideoThumbnailProps {
  videoId: string
  videoStatus: string
  thumbnailUrl?: string
  className?: string
}

type ThumbnailState =
  | { phase: 'idle' }
  | { phase: 'loading' }
  | { phase: 'ready'; url: string }
  | { phase: 'video-fallback'; url: string }
  | { phase: 'error' }

export function VideoThumbnail({ videoId, videoStatus, thumbnailUrl, className }: VideoThumbnailProps) {
  const containerRef = useRef<HTMLDivElement>(null)

  // Check cache first
  const cached = thumbnailCache.get(videoId)
  const initialState: ThumbnailState = thumbnailUrl
    ? { phase: 'ready', url: thumbnailUrl }
    : cached
      ? cached.type === 'image'
        ? { phase: 'ready', url: cached.url }
        : { phase: 'video-fallback', url: cached.url }
      : { phase: 'idle' }

  const [state, setState] = useState<ThumbnailState>(initialState)
  const fetchedRef = useRef(!!thumbnailUrl || !!cached)

  useEffect(() => {
    if (thumbnailUrl) {
      setState({ phase: 'ready', url: thumbnailUrl })
      thumbnailCache.set(videoId, { url: thumbnailUrl, type: 'image' })
      fetchedRef.current = true
      return
    }

    // Check cache again in case it was populated by another instance
    const cachedEntry = thumbnailCache.get(videoId)
    if (cachedEntry) {
      setState(
        cachedEntry.type === 'image'
          ? { phase: 'ready', url: cachedEntry.url }
          : { phase: 'video-fallback', url: cachedEntry.url },
      )
      fetchedRef.current = true
      return
    }

    if (videoStatus !== 'ready' || fetchedRef.current) return

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !fetchedRef.current) {
          fetchedRef.current = true
          observer.disconnect()

          setState({ phase: 'loading' })

          getCompanyVideo(videoId)
            .then((res) => {
              if (res.thumbnailUrl) {
                thumbnailCache.set(videoId, { url: res.thumbnailUrl, type: 'image' })
                setState({ phase: 'ready', url: res.thumbnailUrl })
                return
              }
              const videoUrl = res.downloadUrl
              if (!videoUrl) {
                setState({ phase: 'error' })
                return
              }
              thumbnailCache.set(videoId, { url: videoUrl, type: 'video' })
              setState({ phase: 'video-fallback', url: videoUrl })
            })
            .catch(() => {
              setState({ phase: 'error' })
            })
        }
      },
      { rootMargin: '200px' },
    )

    if (containerRef.current) observer.observe(containerRef.current)
    return () => observer.disconnect()
  }, [videoId, videoStatus, thumbnailUrl])

  const handleImageError = () => {
    // On image load error, show placeholder
    setState({ phase: 'error' })
  }

  const handleVideoError = () => {
    // On video load error, show placeholder
    setState({ phase: 'error' })
  }

  return (
    <div ref={containerRef} className={className}>
      {state.phase === 'ready' && (
        <img
          src={state.url}
          alt="video thumbnail"
          className="w-full h-full object-cover"
          draggable={false}
          onError={handleImageError}
        />
      )}
      {state.phase === 'video-fallback' && (
        <video
          src={state.url}
          muted
          preload="metadata"
          className="w-full h-full object-cover pointer-events-none"
          onLoadedData={(e) => {
            const el = e.currentTarget
            el.currentTime = 0.5
          }}
          onError={handleVideoError}
        />
      )}
      {state.phase === 'loading' && (
        <div className="w-full h-full flex items-center justify-center bg-gray-100">
          <Loader2 className="w-6 h-6 text-gray-400 animate-spin" />
        </div>
      )}
      {(state.phase === 'idle' || state.phase === 'error') && (
        <div className="w-full h-full flex flex-col items-center justify-center bg-gray-100">
          <Film className="w-8 h-8 text-gray-300" />
          <span className="text-[9px] text-gray-400 mt-1">No preview</span>
        </div>
      )}
    </div>
  )
}

export default VideoThumbnail
